"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { Typography } from "../shared/Typography";
import { useQuery } from "@tanstack/react-query";
import { fetchNewsByIds } from "@/services/newsService";
import { getImageUrl } from "@/utils/getImageUrl";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { getLabel } from "@/data/labels";
import { getLocaleFromPathname } from "@/utils/getLocaleFromPath";
import { localeFromNewsLanguage } from "@/utils/localeFromNewsLanguage";

export default function RelatedNewsComponent({ newsIds }: { newsIds: string[] }) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const language = (locale?.split("-")[0] || "en") as AppLocale;
  const { data, error, isFetching } = useQuery({
    queryKey: ["fetchNewsByIds", newsIds],
    queryFn: () => fetchNewsByIds({ newsIds }),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="bg-[#f0f0f0] dark:bg-[#6d6d6d] p-4 rounded">
      <Typography variant="h6">{getLabel(language, "RELATED_NEWS")}</Typography>
      {isFetching ? (
        <div className="grid grid-cols-1 mt-4 sm:grid-cols-2 gap-x-5 gap-y-6 sm:gap-y-7">
          {Array(newsIds?.length)
            .fill(0)
            ?.map((_, index) => {
              return (
                <div key={index} className="flex items-start gap-3 group">
                  <div className="relative w-[30%] min-w-[30%] aspect-[16/10] rounded-md overflow-hidden bg-slate-300 dark:bg-slate-600 animate-pulse"></div>
                  <div className="flex-1">
                    <span className="block h-[13px] w-full bg-slate-300 dark:bg-slate-600 animate-pulse"></span>
                    <span className="block h-[13px] w-full mt-[6px] bg-slate-300 dark:bg-slate-600 animate-pulse"></span>
                    <span className="block h-[13px] w-[60%] mt-[6px] bg-slate-300 dark:bg-slate-600 animate-pulse"></span>
                  </div>
                </div>
              );
            })}
        </div>
      ) : error ? (
        <div className="py-4">
          <Typography variant="body2" className="text-center">
            Failed to fetch!
          </Typography>
        </div>
      ) : (
        <div className="grid grid-cols-1 mt-4 sm:grid-cols-2 gap-x-5 gap-y-6 sm:gap-y-7">
          {data?.map((news, index) => {
            return (
              <div key={index} className="flex items-start gap-3 group">
                <div className="relative w-[30%] min-w-[30%] aspect-[16/10] rounded-md overflow-hidden">
                  <img
                    src={getImageUrl(news?.image?.sizes?.ld?.filename || news?.image?.filename)}
                    alt={news?.image?.alt ?? ""}
                    className="object-cover w-full h-full img-zoom-hover"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1">
                  <Link
                    href={getNewsDetailsUrl(
                      news?.categoryId,
                      news?.subCategoryId,
                      news?.slug,
                      localeFromNewsLanguage(news?.language, locale),
                    )}
                    aria-label={`View news: ${news?.title}`}
                  >
                    <h4 className="text-[13px] font-[500] line-clamp-3 dark:text-white">
                      {news?.title}
                    </h4>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
